import { useCallback, useEffect, useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import {
  Activity,
  AlertTriangle,
  Bell,
  FileText,
  Loader2,
  Search,
  Settings,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  fetchAdminHistory,
  fetchEntityHistory,
  getPatientHistoryDateRange,
  type HistoryResourceType,
  type PatientHistoryActivityType,
  type PatientHistoryCategory,
  type PatientHistoryItem,
  type PatientHistoryTitlePart,
} from "@/utils/patientHistoryApi";

type PatientHistoryDayGroup = {
  dateKey: string;
  dateLabel: string;
  items: PatientHistoryItem[];
};

type PatientHistoryTimelineProps = {
  patientId?: string;
  caretakerId?: string;
  providerId?: string;
  practitionerId?: string;
  resourceType?: HistoryResourceType;
  enabled?: boolean;
  /** Admin-wide feed from `/admin/history`. */
  variant?: "entity" | "admin";
  organizationId?: string | null;
};

const SOURCE_SHEET_CHIPS: { value: string; label: string }[] = [
  { value: "all", label: "All" },
  { value: "patient", label: "Patient" },
  { value: "caretaker", label: "Caretaker" },
];

const FILTER_CHIPS: { value: PatientHistoryCategory; label: string }[] = [
  { value: "management", label: "Management" },
  { value: "notifications", label: "Notifications" },
  { value: "clinical", label: "Clinical" },
  { value: "device", label: "Device" },
  { value: "support", label: "Support" },
  { value: "access", label: "Access" },
];

const ACTIVITY_META: Record<
  PatientHistoryActivityType,
  { icon: typeof Bell; iconClass: string; ringClass: string }
> = {
  triage_alert: {
    icon: AlertTriangle,
    iconClass: "text-[#3463AA]",
    ringClass: "border-[#C5D9F0] bg-[#E8F0FC]",
  },
  notification: {
    icon: Bell,
    iconClass: "text-[#3463AA]",
    ringClass: "border-[#C5D9F0] bg-[#E8F0FC]",
  },
  device_reading: {
    icon: Activity,
    iconClass: "text-[#1B7A4A]",
    ringClass: "border-[#B8E6C8] bg-[#E8F6EE]",
  },
  careplan_edit: {
    icon: FileText,
    iconClass: "text-[#1B7A4A]",
    ringClass: "border-[#B8E6C8] bg-[#E8F6EE]",
  },
  nursing_note: {
    icon: FileText,
    iconClass: "text-[#1B7A4A]",
    ringClass: "border-[#B8E6C8] bg-[#E8F6EE]",
  },
  settings: {
    icon: Settings,
    iconClass: "text-[#3463AA]",
    ringClass: "border-[#C5D9F0] bg-[#E8F0FC]",
  },
};

const DATE_RANGE_OPTIONS = [
  { value: "7", label: "Last 7 Days" },
  { value: "30", label: "Last 30 Days" },
  { value: "90", label: "Last 90 Days" },
  { value: "all", label: "All time" },
] as const;

const PAGE_LIMIT = 50;

function groupByDay(items: PatientHistoryItem[]): PatientHistoryDayGroup[] {
  const map = new Map<string, PatientHistoryDayGroup>();
  for (const item of items) {
    const dateKey =
      item.groupDateKey ?? format(parseISO(item.occurredAt), "yyyy-MM-dd");
    const dateLabel =
      item.groupDateLabel ??
      format(parseISO(`${dateKey}T12:00:00`), "MMM d, yyyy").toUpperCase();

    const bucket = map.get(dateKey);
    if (bucket) {
      bucket.items.push(item);
    } else {
      map.set(dateKey, { dateKey, dateLabel, items: [item] });
    }
  }

  return Array.from(map.values())
    .sort((a, b) => b.dateKey.localeCompare(a.dateKey))
    .map((group) => ({
      ...group,
      items: [...group.items].sort(
        (a, b) =>
          parseISO(b.occurredAt).getTime() - parseISO(a.occurredAt).getTime(),
      ),
    }));
}

function DateSeparator({ label }: { label: string }) {
  return (
    <div className="mb-3 flex items-center gap-3 pt-1">
      <span className="shrink-0 text-[11px] font-semibold tracking-[0.06em] text-[#9CA3AF]">
        {label}
      </span>
      <div className="h-px flex-1 bg-[#E5E7EB]" />
    </div>
  );
}

function ActivityTitle({ parts }: { parts: PatientHistoryTitlePart[] }) {
  return (
    <span className="text-sm font-medium leading-snug text-[#111827]">
      {parts.map((part, i) =>
        part.bold ? (
          <span key={i} className="font-semibold text-[#111827]">
            {part.text}
          </span>
        ) : (
          <span key={i}>{part.text}</span>
        ),
      )}
    </span>
  );
}

function TimelineRow({
  item,
  showConnectorBelow,
  removeSeperateor,
}: {
  item: PatientHistoryItem;
  showConnectorBelow: boolean;
  removeSeperateor: boolean;
}) {
  const meta = ACTIVITY_META[item.type];
  const Icon = meta.icon;
  const timeLabel =
    item.displayTime ?? format(parseISO(item.occurredAt), "h:mm a");

  return (
    <div
      className={cn(
        "relative grid grid-cols-[28px_minmax(0,1fr)] gap-x-3",
        showConnectorBelow ? "pb-5" : "pb-1",
      )}
    >
      <div className="relative self-stretch">
        <div className="flex justify-center pt-0.5">
          <div
            className={cn(
              "relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border bg-white",
              meta.ringClass,
            )}
          >
            <Icon className={cn("h-3.5 w-3.5", meta.iconClass)} strokeWidth={2} />
          </div>
        </div>
        {showConnectorBelow && !removeSeperateor ? (
          <div
            className="absolute left-1/2 top-[calc(0.125rem+0.875rem)] -bottom-[calc(1.25rem+0.125rem+0.875rem)] z-0 w-px -translate-x-1/2 bg-[#E5E7EB]"
            aria-hidden
          />
        ) : null}
      </div>

      <div className="min-w-0">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <ActivityTitle parts={item.titleParts} />
          </div>
          <span className="shrink-0 pt-0.5 text-xs font-normal text-[#9CA3AF]">
            {timeLabel}
          </span>
        </div>

        {item.subtitle ? (
          <p className="mt-1 text-xs leading-relaxed text-[#6B7280]">
            {item.subtitle}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export default function PatientHistoryTimeline({
  patientId,
  caretakerId,
  providerId,
  practitionerId,
  resourceType,
  enabled = true,
  variant = "entity",
  organizationId,
}: PatientHistoryTimelineProps) {
  const isAdminFeed = variant === "admin";
  const resolvedResourceType: HistoryResourceType =
    resourceType ??
    (practitionerId
      ? "practitioner"
      : providerId
        ? "provider"
        : caretakerId
          ? "caretaker"
          : "patient");
  const resourceId =
    practitionerId ?? providerId ?? caretakerId ?? patientId;
  const historyLabel = isAdminFeed
    ? "activity timeline"
    : resolvedResourceType === "caretaker"
      ? "caretaker history"
      : resolvedResourceType === "provider"
        ? "provider history"
        : resolvedResourceType === "practitioner"
          ? "practitioner history"
          : "patient history";

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateRange, setDateRange] = useState<string>("30");
  const [sourceSheet, setSourceSheet] = useState("all");
  const [selectedCategories, setSelectedCategories] = useState<
    PatientHistoryCategory[]
  >([]);
  const [items, setItems] = useState<PatientHistoryItem[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 400);
    return () => window.clearTimeout(timer);
  }, [search]);

  const loadHistory = useCallback(
    async (nextPage: number, append: boolean) => {
      if (!enabled) return;
      if (!isAdminFeed && !resourceId) return;

      append ? setLoadingMore(true) : setLoading(true);
      setError(null);

      try {
        const query = {
          search: debouncedSearch || undefined,
          ...getPatientHistoryDateRange(dateRange),
          page: nextPage,
          limit: PAGE_LIMIT,
        };

        const result = isAdminFeed
          ? await fetchAdminHistory({
              ...query,
              sourceSheet:
                sourceSheet === "all" ? undefined : sourceSheet,
              organizationId: organizationId?.trim() || undefined,
            })
          : await fetchEntityHistory(resolvedResourceType, resourceId!, query);
        
        setItems((prev) =>
          append ? [...prev, ...result.items] : result.items,
        );
        setPage(result.page);
        setHasMore(result.hasMore);
      } catch (err: unknown) {
        const message =
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ||
          (err as { message?: string })?.message ||
          "Failed to load history.";
        setError(message);
        if (!append) setItems([]);
        setHasMore(false);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [
      resourceId,
      resolvedResourceType,
      enabled,
      debouncedSearch,
      dateRange,
      isAdminFeed,
      sourceSheet,
      organizationId,
    ],
  );

  useEffect(() => {
    if (!enabled) return;
    if (!isAdminFeed && !resourceId) return;
    void loadHistory(1, false);
  }, [
    resourceId,
    enabled,
    debouncedSearch,
    dateRange,
    loadHistory,
    isAdminFeed,
    sourceSheet,
    organizationId,
  ]);

  const toggleCategory = (category: PatientHistoryCategory) => {
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category],
    );
  };

  const filteredGroups = useMemo(() => {
    const filtered =
      selectedCategories.length === 0
        ? items
        : items.filter((item) => selectedCategories.includes(item.category));

    return groupByDay(filtered);
  }, [items, selectedCategories]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 border-b border-[#E5E7EB] bg-white pb-4 pt-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9CA3AF]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search activity..."
              className="h-9 rounded-lg border-[#E5E7EB] bg-white pl-9 text-sm shadow-none"
            />
          </div>
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="h-9 w-full rounded-lg border-[#E5E7EB] bg-white text-sm shadow-none sm:w-[9.5rem]">
              <SelectValue placeholder="Date range" />
            </SelectTrigger>
            <SelectContent>
              {DATE_RANGE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {isAdminFeed ? (
          <div className="mt-3 flex flex-wrap gap-2">
            {SOURCE_SHEET_CHIPS.map((chip) => {
              const isActive = sourceSheet === chip.value;
              return (
                <button
                  key={chip.value}
                  type="button"
                  onClick={() => setSourceSheet(chip.value)}
                  className={cn(
                    "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                    isActive
                      ? "border-[#3463AA] bg-[#E8F0FC] text-[#3463AA]"
                      : "border-[#E5E7EB] bg-white text-[#374151] hover:border-[#D1D5DB]",
                  )}
                >
                  {chip.label}
                </button>
              );
            })}
          </div>
        ) : null}

        <div className="mt-3 flex flex-wrap gap-2">
          {FILTER_CHIPS.map((chip) => {
            const isActive = selectedCategories.includes(chip.value);
            return (
              <button
                key={chip.value}
                type="button"
                onClick={() => toggleCategory(chip.value)}
                className={cn(
                  "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                  isActive
                    ? "border-[#3463AA] bg-[#E8F0FC] text-[#3463AA]"
                    : "border-[#E5E7EB] bg-white text-[#374151] hover:border-[#D1D5DB]",
                )}
              >
                {chip.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pt-4 pr-1">
        {loading ? (
          <div className="flex h-40 items-center justify-center text-sm text-[#6B7280]">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading {historyLabel}...
          </div>
        ) : error ? (
          <div className="flex h-40 flex-col items-center justify-center gap-3 text-sm text-[#6B7280]">
            <p>{error}</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void loadHistory(1, false)}
            >
              Retry
            </Button>
          </div>
        ) : filteredGroups.length === 0 ? (
          <div className="flex h-40 items-center justify-center rounded-lg border border-dashed border-[#E5E7EB] text-sm text-[#6B7280]">
            No activity found for this period.
          </div>
        ) : (
          <div className="space-y-5">
            {filteredGroups.map((group) => (
              <section key={group.dateKey}>
                <DateSeparator label={group.dateLabel} />
                <div>
                  {group.items.map((item, itemIndex) => (
                    <TimelineRow
                      key={item.id}
                      item={item}
                      showConnectorBelow={itemIndex < group.items.length - 1}
                      removeSeperateor={item?.titleParts[0]?.text?.includes('outbound call started') || item?.titleParts[0]?.text?.includes('inbound call started') }
                    />
                  ))}
                </div>
              </section>
            ))}

            {hasMore ? (
              <div className="flex justify-center pb-2 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={loadingMore}
                  onClick={() => void loadHistory(page + 1, true)}
                >
                  {loadingMore ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    "Load more"
                  )}
                </Button>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
